import {customElement, property, query, queryAll, state} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {ScrollableBar} from "./scrollableBar";
import {trackingButtonInteract} from "../../tracking/trackingAttributes";
import {templateContent} from "lit/directives/template-content.js";
import type {DirectiveResult} from "lit/directive";

@customElement("eop-tabs")
export class EopTabs extends ScrollableBar {

    @property({attribute: "tab-layout", type: String})
    public layout: string = "horizontal";
    @property({attribute: "baseplate-enabled", reflect: true, type: Boolean})
    public baseplateEnabled: boolean = false;
    @query(".tabs")
    protected scrollableArea: HTMLElement;
    @queryAll(".tab")
    private tabs: NodeListOf<HTMLElement>;
    @queryAll(".tab-content")
    private tabContentNodes: NodeListOf<HTMLElement>;
    @state()
    private activeTabIndex: number = 0;

    private tabLabels: string[];
    private tabContents: DirectiveResult[];

    protected items(): HTMLElement[] {
        return Array.from(this.tabs);
    }

    public override connectedCallback(): void {
        super.connectedCallback();
        const templates = Array.from(this.querySelectorAll<HTMLTemplateElement>(".tab-content-template"));
        this.tabLabels = templates.map(it => it.content.querySelector(".tab-content")!.getAttribute("title")!);
        this.tabContents = templates.map(it => templateContent(it));
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.tabContentNodes.forEach((tabContent, index) => {
            if (index === 0) {
                tabContent.classList.add("active");
            }
            if (this.layout === "vertical") {
                tabContent.classList.add("layout-element-nested-8");
            }
        });
    }

    public render(): TemplateResult {
        return html`
            <div class="tab-container ${this.layout} ${this.baseplateEnabled ? "reverse-background" : ""}">
                <div class="tab-navigation" aria-label=${this.dictionary.translate("MSG_TAB_NAVIGATION")}>
                    <div role="tablist" class="tabs horizontally-scrollable-area"
                         @scroll=${this.toggleScrollButtonVisibility}
                         @touchend=${{handleEvent: () => this.toggleScrollButtonVisibility(), passive: true}}
                    >
                        ${(this.renderTabLabels())}
                        <div aria-hidden="true">
                            <eop-swipe-hint></eop-swipe-hint>
                        </div>
                    </div>
                    ${this.renderScrollLeftButton()}
                    ${this.renderScrollRightButton()}
                </div>
                <div class="tab-contents">
                    ${this.tabContents}
                </div>
            </div>
        `;
    }

    private renderTabLabels(): TemplateResult[] {
        return this.tabLabels.map((tabLabel, index) => {
            const handleTabClick = (e: Event): void => this.clickTab(e, index);
            return html`
                <button role="tab"
                        class="tab ${this.isTabActive(index) ? "active" : ""}"
                        aria-selected=${this.isTabActive(index)}
                        @click=${handleTabClick}
                        ${trackingButtonInteract(tabLabel)}
                >
                    <span class="tab-label">${tabLabel}</span>
                </button>`;
        });
    }

    private clickTab(event: Event, index: number): void {
        event.preventDefault();

        this.activeTabIndex = index;
        this.tabContentNodes.forEach(tabContent => tabContent.classList.remove("active"));
        this.tabContentNodes[index].classList.add("active");
        this.scrollToItem(index);
    }

    private isTabActive(index: number): boolean {
        return this.activeTabIndex === index;
    }
}